import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '@store/store'
import { Field } from '@app-types/entity_types'
import { MicroFieldDef } from '@app-types/general'
import { getSortedFields } from '@selectors/data/field_selectors'

export const filteringOperators = ['Contains', 'Excludes', 'Exactly Matches'] as const
export type FilteringOperator = (typeof filteringOperators)[number]

export type FilteringParams = {
  fieldId: string
  operator?: FilteringOperator
  value: string | string[] // Support both string and array values
  viewId: string
}

export type Filter = Omit<FilteringParams, 'viewId'>

export type RemoveFilter = {
  index: number
  viewId: string
}

type State = {
  params: Record<string, Filter[]>
}

const initialState: State = {
  params: {},
}

export const filteringSlice = createSlice({
  name: 'filtering',
  initialState,
  reducers: {
    addFilterParams: (state, action: PayloadAction<FilteringParams & { index: number }>) => {
      const { fieldId, viewId, operator, value, index } = action.payload
      const viewParams = state.params[viewId] ?? []
      const alreadyAdded = Boolean(viewParams[index])

      if (alreadyAdded) {
        const existing = state.params[viewId][index]
        state.params[viewId][index] = {
          fieldId: fieldId ?? existing.fieldId,
          operator: operator !== undefined ? operator : existing.operator,
          value: value !== undefined ? value : existing.value,
        }
      } else {
        viewParams.push({
          fieldId,
          operator,
          value,
        })

        state.params[viewId] = [...viewParams]
      }
    },
    removeFilterParams: (state, action: PayloadAction<RemoveFilter>) => {
      const { viewId, index } = action.payload

      state.params[viewId] = state.params[viewId].filter((_, paramIndx) => paramIndx !== index)
    },
    resetFilterParams: (state, action: PayloadAction<string>) => {
      state.params[action.payload] = []
    },
    cleanupFilterParams: (state, action: PayloadAction<string>) => {
      const fieldIdToRemove = action.payload

      // Loop through all views and remove any filter params that reference the deleted field
      Object.keys(state.params).forEach(viewId => {
        state.params[viewId] = state.params[viewId].filter(filter => filter.fieldId !== fieldIdToRemove)
      })
    },
  },
})

export const { addFilterParams, removeFilterParams, resetFilterParams, cleanupFilterParams } = filteringSlice.actions

export default filteringSlice.reducer

/**
 * @name getFilterParms
 * @description: Retuns an array of selected filters for a specified View. Defaults to an empty array, if no filters are set
 */
export const getFilterParams = createSelector(
  [(state: RootState) => state.filtering?.params, (_: RootState, props: { viewId: string }) => props.viewId],
  (filterParams, viewId: string) => {
    if (filterParams && filterParams[viewId]) {
      return filterParams[viewId]
    } else {
      return []
    }
  }
)

/**
 * @name getValidFilterParams
 * @description Returns only those filters that are complete and can be applied:
 *  - fieldId is truthy (non-empty string)
 *  - operator is one of the known filteringOperators
 *  - value is not empty (string with non-whitespace characters OR non-empty array)
 */
export const getValidFilterParams = createSelector([getFilterParams], (filters: Filter[]) => {
  return filters.filter(filter => {
    const hasField = Boolean(filter.fieldId)
    const hasOperator = Boolean(filter.operator && filteringOperators.includes(filter.operator))
    const hasValue = Array.isArray(filter.value)
      ? filter.value.length > 0
      : typeof filter.value === 'string'
        ? filter.value.trim().length > 0
        : false

    return hasField && hasOperator && hasValue
  })
})

/**
 * @name getFilteringColsDef
 * @description Returns a list of filterable fields for a specific table. This data is used to populate the filtering component
 */
export const getFilteringColsDef = createSelector([getSortedFields], fields => {
  // need to cast to Field. This is to workaround issues from mixing js & ts
  const tableFields = fields as Field[]

  const filterableFields = tableFields.filter(({ attributes }) => {
    const { filterable } = attributes
    return filterable
  })

  return filterableFields.map(
    ({ id, attributes }): MicroFieldDef => ({
      fieldId: id.toString(),
      dataType: attributes.variant_type,
      name: attributes.name,
    })
  )
})
